import React from "react";
import Avatar from "@rio-cloud/rio-uikit/Avatar";
import TableCol from "@rio-cloud/rio-uikit/TableCol";
import NoData from "@rio-cloud/rio-uikit/NoData";
import { FuelingRecord } from "./mock";
import SharedTable, { resolveDisplaySortKey } from "../SharedTable";
import type { SharedTableBaseProps } from "../SharedTable";
import { useT } from "@hooks";

const SKELETON_CELL_HEIGHT = 18;
const PLACEHOLDER_ROWS = 5;

type SortKey<T> = keyof T | [keyof T, keyof T];

const DETAIL_BUTTON_STYLE = {
  background: "none",
  border: 0,
  appearance: "none",
  font: "inherit",
  color: "inherit",
  textAlign: "inherit",
  cursor: "pointer",
} as const;

interface Props extends SharedTableBaseProps<FuelingRecord, SortKey<FuelingRecord>> {
  onViewDetail?: (id: string) => void;
}

const Table = ({
  rows,
  isViewTypeTable,
  tableRef,
  tableClassNames,
  columns,
  columnLabels,
  columnsDetails,
  sortKey,
  sortDirection,
  handleSortChange,
  onActiveRowChange,
  onViewDetail,
  isLoading = false,
}: Props) => {
  const t = useT();

  const renderCellContent = (row: FuelingRecord, col: string) => {
    const value = row[col as keyof FuelingRecord];

    if (col === "driver") {
      const getGravatarUrl = (name: string) => {
        const email = name.toLowerCase().trim().replace(/\s+/g, ".") + "@example.com";
        let hash = 0;
        for (let i = 0; i < email.length; i++) {
          const char = email.codePointAt(i) as number;
          hash = (hash << 5) - hash + char;
          hash = hash & hash;
        }
        const hashString = Math.abs(hash).toString(16).padStart(8, "0");
        return `https://www.gravatar.com/avatar/${hashString}?d=identicon&s=80`;
      };

      const gravatarUrl = row.driverPhoto || getGravatarUrl(row.driver);

      return (
        <div className="display-flex flex-row align-items-center gap-10" style={{ minWidth: 0 }}>
          <Avatar>
            <img
              src={gravatarUrl}
              alt={row.driver}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "2px solid white",
                boxShadow: "0 0 0 1px #E0E0E0",
                objectFit: "cover",
              }}
            />
          </Avatar>
          <div className="display-flex flex-column gap-5" style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
            <span
              className="text-medium text-size-14"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: "1.4",
              }}
            >
              {row.driver}
            </span>
          </div>
        </div>
      );
    }

    if (col === "fuelType") {
      return <span>{row.fuelType}</span>;
    }

    if (col === "quantity") {
      return row.quantityUnit ? `${row.quantity} ${row.quantityUnit}` : row.quantity;
    }

    return value ?? <NoData text="-" />;
  };

  return (
    <SharedTable<FuelingRecord>
      rows={rows}
      isViewTypeTable={isViewTypeTable}
      tableRef={tableRef}
      tableClassNames={tableClassNames}
      columns={columns}
      columnsDetails={columnsDetails}
      columnLabels={columnLabels}
      sortBy={resolveDisplaySortKey(sortKey)}
      sortDir={sortKey ? sortDirection : undefined}
      handleSortChange={handleSortChange}
      onActiveRowChange={onActiveRowChange}
      notFoundMessage={t("fueling.table.notFound")}
      containerClassName="table-responsive min-height-300"
      isLoading={isLoading}
      skeletonCellHeight={SKELETON_CELL_HEIGHT}
      placeholderRows={PLACEHOLDER_ROWS}
      renderColumnCol={(column) =>
        column === "driver" ? (
          <col key={column} style={{ width: "230px", minWidth: "230px" }} />
        ) : (
          <TableCol key={column} columnDetails={columnsDetails[column]} />
        )
      }
      renderCell={renderCellContent}
      renderRowActions={(row) => (
        <td className="table-action">
          <button
            type="button"
            className="rioglyph rioglyph-info-sign text-size-18 text-color-primary cursor-pointer"
            style={DETAIL_BUTTON_STYLE}
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onViewDetail?.(row.id);
            }}
            onKeyDown={(evento) => {
              if (evento.key === "Enter" || evento.key === " ") {
                evento.preventDefault();
                evento.stopPropagation();
                onViewDetail?.(row.id);
              }
            }}
            title={t("fueling.table.action.viewDetail")}
          />
        </td>
      )}
    />
  );
};

export default Table;
